<template>
    <v-container fluid class="sa-admin pa-4 pa-md-6">
        <div class="d-flex flex-wrap align-center mb-4">
            <div>
                <h1 class="text-h5 font-weight-bold mb-1">Sales Assessments</h1>
                <div class="text-body-2 grey--text text--darken-1">
                    Candidates who took the sales potential assessment. Scores are a guide for the interview; a person
                    makes every decision.
                </div>
            </div>
            <v-spacer />
            <v-btn text class="text-none mt-2 mt-md-0" :loading="loading" @click="load">
                <v-icon left>mdi-refresh</v-icon> Refresh
            </v-btn>
        </div>

        <!-- Share link -->
        <v-card outlined class="mb-4 pa-4 rounded-lg">
            <div class="d-flex flex-wrap align-center" style="gap: 12px">
                <v-icon color="#3949ab">mdi-link-variant</v-icon>
                <div class="flex-grow-1" style="min-width: 240px">
                    <div class="text-caption grey--text text--darken-1">Shareable candidate link</div>
                    <div class="text-body-1 font-weight-medium sa-link">{{ shareLink }}</div>
                </div>
                <v-text-field v-model="sourceTag" dense outlined hide-details label="Track source (optional)"
                    placeholder="e.g. indeed" style="max-width: 220px" />
                <v-btn depressed color="#3949ab" dark class="text-none" @click="copyLink">
                    <v-icon left small>mdi-content-copy</v-icon> Copy link
                </v-btn>
            </div>
        </v-card>

        <!-- Stat tiles -->
        <div class="sa-tiles mb-4">
            <div class="sa-tile">
                <div class="sa-tile__label">Completed</div>
                <div class="sa-tile__value">{{ completed.length }}</div>
            </div>
            <div v-for="(band, key) in BANDS" :key="key" class="sa-tile sa-tile--clickable"
                :class="{ 'is-active': bandFilter === key }" @click="toggleBand(key)">
                <div class="sa-tile__label">
                    <v-icon small :color="band.color" class="mr-1">{{ band.icon }}</v-icon>{{ band.label }}
                </div>
                <div class="sa-tile__value">{{ completed.filter(a => a.band === key).length }}</div>
            </div>
            <div class="sa-tile">
                <div class="sa-tile__label">Not yet reviewed</div>
                <div class="sa-tile__value">{{ completed.filter(a => a.review_status === 'new').length }}</div>
            </div>
            <div class="sa-tile">
                <div class="sa-tile__label">Started, not finished</div>
                <div class="sa-tile__value">{{ incomplete.length }}</div>
            </div>
        </div>

        <!-- Filters -->
        <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
            <v-text-field v-model="search" dense outlined hide-details prepend-inner-icon="mdi-magnify"
                label="Search name or email" style="max-width: 280px" clearable />
            <v-select v-model="bandFilter" :items="bandItems" dense outlined hide-details label="Recommendation"
                clearable style="max-width: 200px" />
            <v-select v-model="statusFilter" :items="REVIEW_STATUSES" dense outlined hide-details label="Status"
                clearable style="max-width: 180px" />
            <v-switch v-model="showIncomplete" inset dense hide-details label="Show unfinished" class="mt-0" />
            <v-spacer />
            <v-btn outlined class="text-none" :disabled="selected.length < 2 || selected.length > 4"
                @click="compareOpen = true">
                <v-icon left small>mdi-compare-horizontal</v-icon>
                Compare{{ selected.length ? ` (${selected.length})` : '' }}
            </v-btn>
            <v-btn outlined class="text-none" :disabled="!filtered.length" @click="exportCsv">
                <v-icon left small>mdi-download</v-icon> Export CSV
            </v-btn>
        </div>
        <div v-if="selected.length === 1 || selected.length > 4" class="text-caption grey--text mb-2">
            Select 2 to 4 candidates to compare.
        </div>

        <v-card outlined class="rounded-lg">
            <v-data-table v-model="selected" :headers="headers" :items="filtered" :loading="loading" show-select
                item-key="id" sort-by="overall_score" sort-desc :items-per-page="25"
                :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }" class="sa-table"
                no-data-text="No completed assessments yet. Share the link above to get started."
                @click:row="openDetail">
                <template #[`item.name`]="{ item }">
                    <div class="font-weight-medium">{{ item.name }}</div>
                    <div class="text-caption grey--text text--darken-1">
                        {{ item.email }}
                        <span v-if="item.attempts > 1" class="ml-1">· {{ item.attempts }} attempts</span>
                    </div>
                </template>

                <template #[`item.submitted_at`]="{ item }">
                    <span v-if="item.submitted_at">{{ formatDate(item.submitted_at) }}</span>
                    <span v-else class="grey--text">Started {{ formatDate(item.started_at) }}</span>
                </template>

                <template #[`item.overall_score`]="{ item }">
                    <div v-if="item.status === 'in_progress'" class="grey--text text-caption">
                        Unfinished · page {{ item.current_step + 1 }}
                    </div>
                    <div v-else class="d-flex align-center">
                        <span class="sa-score">{{ item.overall_score === null ? '–' : Math.round(item.overall_score) }}</span>
                        <v-tooltip v-if="item.status === 'evaluating'" top>
                            <template #activator="{ on }">
                                <v-progress-circular v-on="on" indeterminate size="14" width="2" color="#2a78d6" class="ml-2" />
                            </template>
                            AI is reading the written answers
                        </v-tooltip>
                        <v-tooltip v-else-if="item.status === 'ai_failed' || item.is_provisional" top>
                            <template #activator="{ on }">
                                <v-icon v-on="on" small color="#c98a00" class="ml-1">mdi-timer-sand</v-icon>
                            </template>
                            Provisional: written answers not yet scored by AI
                        </v-tooltip>
                    </div>
                </template>

                <template #[`item.band`]="{ item }">
                    <span v-if="item.band && BANDS[item.band]" class="sa-band"
                        :style="{ background: BANDS[item.band].tint }">
                        <v-icon x-small :color="BANDS[item.band].color" class="mr-1">{{ BANDS[item.band].icon }}</v-icon>
                        {{ BANDS[item.band].label }}
                    </span>
                </template>

                <template v-for="cat in CATEGORY_ORDER" #[`item.cat_${cat}`]="{ item }">
                    <score-bar :key="cat" dense :value="item.categories[cat]"
                        :tooltip="categories[cat] ? categories[cat].label : ''" />
                </template>

                <template #[`item.flags`]="{ item }">
                    <span v-if="item.flags.length" class="sa-flagcount"
                        :style="{ color: FLAG_LEVELS[topFlagLevel(item.flags)].color }">
                        <v-icon small :color="FLAG_LEVELS[topFlagLevel(item.flags)].color" class="mr-1">
                            {{ FLAG_LEVELS[topFlagLevel(item.flags)].icon }}
                        </v-icon>{{ item.flags.length }}
                    </span>
                    <span v-else class="grey--text">–</span>
                </template>

                <template #[`item.review_status`]="{ item }">
                    <span class="text-body-2">{{ reviewStatusText(item.review_status) }}</span>
                </template>
            </v-data-table>
        </v-card>

        <sales-assessment-detail v-if="detailId" :assessment-id="detailId" @close="detailId = null"
            @changed="load(true)" @deleted="onDeleted" />

        <!-- Compare -->
        <v-dialog v-model="compareOpen" max-width="960" scrollable>
            <v-card>
                <v-card-title class="text-h6 font-weight-bold">
                    Compare candidates
                    <v-spacer />
                    <v-btn icon @click="compareOpen = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <table class="sa-compare">
                        <thead>
                            <tr>
                                <th />
                                <th v-for="c in selected" :key="c.id">
                                    <a class="sa-compare__name" @click="compareOpen = false; openDetail(c)">{{ c.name }}</a>
                                    <div v-if="c.band && BANDS[c.band]" class="mt-1">
                                        <span class="sa-band" :style="{ background: BANDS[c.band].tint }">
                                            <v-icon x-small :color="BANDS[c.band].color" class="mr-1">{{ BANDS[c.band].icon }}</v-icon>
                                            {{ BANDS[c.band].label }}
                                        </span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="sa-compare__overall">
                                <td>Overall score</td>
                                <td v-for="c in selected" :key="c.id">
                                    <score-bar :value="c.overall_score" :bold="isBest('overall', c)" />
                                </td>
                            </tr>
                            <tr v-for="cat in CATEGORY_ORDER" :key="cat">
                                <td>{{ categories[cat] ? categories[cat].label : cat }}</td>
                                <td v-for="c in selected" :key="c.id">
                                    <score-bar dense :value="c.categories[cat]" :bold="isBest(cat, c)" />
                                </td>
                            </tr>
                            <tr>
                                <td>Flags</td>
                                <td v-for="c in selected" :key="c.id" class="text-caption">
                                    <span v-if="!c.flags.length" class="grey--text">None</span>
                                    <div v-for="(flag, i) in c.flags" :key="i" class="d-flex align-center">
                                        <v-icon x-small :color="FLAG_LEVELS[flag.level].color" class="mr-1">
                                            {{ FLAG_LEVELS[flag.level].icon }}
                                        </v-icon>
                                        {{ FLAG_SHORT[flag.code] || flag.code }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="text-caption grey--text mt-3">Bold marks the highest score in each row.</div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import ScoreBar from './ScoreBar.vue'
import SalesAssessmentDetail from './SalesAssessmentDetail.vue'
import { BANDS, FLAG_LEVELS, FLAG_SHORT, REVIEW_STATUSES, CATEGORY_ORDER, reviewStatusText, topFlagLevel } from './assessmentUi'

export default {
    name: 'SalesAssessmentsAdmin',
    components: { ScoreBar, SalesAssessmentDetail },
    data() {
        return {
            BANDS, FLAG_LEVELS, FLAG_SHORT, REVIEW_STATUSES, CATEGORY_ORDER,
            loading: false,
            assessments: [],
            categories: {},
            search: '',
            bandFilter: null,
            statusFilter: null,
            showIncomplete: false,
            selected: [],
            compareOpen: false,
            detailId: null,
            sourceTag: '',
            pollTimer: null,
        }
    },
    computed: {
        shareLink() {
            const base = (process.env.VUE_APP_URL || window.location.origin + '/').replace(/\/?$/, '/')
            const source = this.sourceTag.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, '')
            return `${base}work-style${source ? '?source=' + source : ''}`
        },
        completed() {
            return this.assessments.filter(a => a.status !== 'in_progress')
        },
        incomplete() {
            return this.assessments.filter(a => a.status === 'in_progress')
        },
        bandItems() {
            return Object.keys(BANDS).map(key => ({ value: key, text: BANDS[key].label }))
        },
        filtered() {
            const term = (this.search || '').toLowerCase()
            return (this.showIncomplete ? this.assessments : this.completed).filter(a =>
                (!term || a.name.toLowerCase().includes(term) || a.email.toLowerCase().includes(term))
                && (!this.bandFilter || a.band === this.bandFilter)
                && (!this.statusFilter || a.review_status === this.statusFilter))
        },
        headers() {
            const short = {
                resilience: 'Resil.', coachability: 'Coach.', drive: 'Drive', work_ethic: 'Work',
                empathy: 'Listen', persuasion: 'Persu.', integrity: 'Integ.', communication: 'Comms',
            }
            return [
                { text: 'Candidate', value: 'name' },
                { text: 'Date', value: 'submitted_at' },
                { text: 'Score', value: 'overall_score' },
                { text: 'Recommendation', value: 'band', sort: (a, b) => this.bandRank(a) - this.bandRank(b) },
                ...CATEGORY_ORDER.map(cat => ({
                    text: short[cat], value: `cat_${cat}`, width: 96,
                    sort: (a, b) => (a ?? -1) - (b ?? -1),
                })),
                { text: 'Flags', value: 'flags', sortable: false },
                { text: 'Status', value: 'review_status' },
            ]
        },
    },
    watch: {
        showIncomplete() {
            this.load()
        },
    },
    created() {
        this.load()
    },
    beforeDestroy() {
        clearTimeout(this.pollTimer)
    },
    methods: {
        reviewStatusText,
        topFlagLevel,
        async load(silent = false) {
            if (!silent) this.loading = true
            const res = await this.$axios.get('api/sales-assessments', { include_incomplete: this.showIncomplete ? 1 : 0 })
            this.loading = false
            if (!res.data || !res.data.response) {
                if (!silent) this.$toast.error((res.data && res.data.message) || 'Could not load assessments')
                return
            }
            this.categories = res.data.categories
            // Flatten category scores so the table can sort on them.
            this.assessments = res.data.assessments.map(a => {
                const row = { ...a, categories: a.categories || {} }
                CATEGORY_ORDER.forEach(cat => { row[`cat_${cat}`] = row.categories[cat] ?? null })
                return row
            })
            const ids = this.assessments.map(a => a.id)
            this.selected = this.selected
                .filter(s => ids.includes(s.id))
                .map(s => this.assessments.find(a => a.id === s.id))

            // Keep the list fresh while the AI is still evaluating someone.
            clearTimeout(this.pollTimer)
            if (this.assessments.some(a => a.status === 'evaluating')) {
                this.pollTimer = setTimeout(() => this.load(true), 15000)
            }
        },
        bandRank(band) {
            return { high_potential: 3, potential: 2, low_potential: 1 }[band] || 0
        },
        toggleBand(key) {
            this.bandFilter = this.bandFilter === key ? null : key
        },
        openDetail(item) {
            this.detailId = item.id
        },
        onDeleted() {
            this.detailId = null
            this.load()
        },
        isBest(key, candidate) {
            const value = key === 'overall' ? candidate.overall_score : candidate.categories[key]
            if (value === null || value === undefined) return false
            const values = this.selected.map(c => (key === 'overall' ? c.overall_score : c.categories[key]) ?? -1)
            return value === Math.max(...values)
        },
        formatDate(value) {
            return value ? this.$date(value).format('D MMM YYYY') : ''
        },
        async copyLink() {
            try {
                await navigator.clipboard.writeText(this.shareLink)
                this.$toast.success('Link copied')
            } catch (e) {
                this.$toast.info(this.shareLink)
            }
        },
        exportCsv() {
            const cols = [
                ['Name', a => a.name],
                ['Email', a => a.email],
                ['Source', a => a.source || ''],
                ['Submitted', a => a.submitted_at ? this.$date(a.submitted_at).format('YYYY-MM-DD HH:mm') : ''],
                ['Overall score', a => a.overall_score ?? ''],
                ['Recommendation', a => (BANDS[a.band] && BANDS[a.band].label) || ''],
                ['Provisional', a => (a.is_provisional ? 'yes' : 'no')],
                ...CATEGORY_ORDER.map(cat => [this.categories[cat] ? this.categories[cat].label : cat, a => a.categories[cat] ?? '']),
                ['Consistency', a => a.consistency_score ?? ''],
                ['Flags', a => a.flags.map(f => FLAG_SHORT[f.code] || f.code).join('; ')],
                ['Status', a => reviewStatusText(a.review_status)],
                ['Attempts', a => a.attempts],
            ]
            const escape = v => {
                const s = String(v)
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
            }
            const lines = [cols.map(c => escape(c[0])).join(',')]
            this.filtered.forEach(a => lines.push(cols.map(c => escape(c[1](a))).join(',')))
            const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = `sales-assessments-${this.$date().format('YYYY-MM-DD')}.csv`
            link.click()
            URL.revokeObjectURL(link.href)
        },
    },
}
</script>

<style scoped>
.sa-flagcount {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    font-size: 14px;
}

.sa-link {
    word-break: break-all;
    color: #1a237e;
}

.sa-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
}

.sa-tile {
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    padding: 12px 16px;
}

.sa-tile--clickable {
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.sa-tile--clickable:hover,
.sa-tile.is-active {
    border-color: #2a78d6;
    box-shadow: 0 0 0 2px rgba(42, 120, 214, 0.15);
}

.sa-tile__label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
}

.sa-tile__value {
    font-size: 26px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
    font-variant-numeric: tabular-nums;
}

.sa-score {
    font-size: 18px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.sa-band {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
}

.sa-table ::v-deep tbody tr {
    cursor: pointer;
}

.sa-compare {
    width: 100%;
    border-collapse: collapse;
}

.sa-compare th,
.sa-compare td {
    padding: 8px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    text-align: left;
    vertical-align: top;
}

.sa-compare td:first-child {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
    white-space: nowrap;
}

.sa-compare__overall td {
    background: #f6f9fe;
}

.sa-compare__name {
    font-weight: 600;
    color: #1a237e;
}
</style>
